import {html, type TemplateResult} from "lit";
import {customElement, property, query, queryAll, state} from "lit/decorators.js";
import Styles from "./dialog.lit.scss";
import {UnLitElement} from "../../../common/elements";
import {Dictionary, type DictionaryElement} from "../dictionary";
import {EventBus} from "../../../common/eventBus";
import {resolve} from "../../../container";
import {eventOccurredOutside, onTabbingOutOfDialog} from "../../../common/utils/events";
import {Page} from "../../../common/page";
import {BootStrapDialogOpener, CLEANUP_DIALOG_EVENT} from "./bootStrapDialogOpener";
import {waitTime} from "../../../common/utils/promises";

const PANEL_SIZE_FACTOR = 1.5;
const CLOSING_ANIMATION_DURATION_IN_MILLIS = 500;


@customElement("eop-modal-dialog")
export class EopModalDialog extends UnLitElement {

    public static readonly styles = Styles;

    @queryAll(".layer-section")
    private layerSections: NodeListOf<HTMLElement>;
    @query("dialog")
    private dialog: HTMLDialogElement;
    @query(".dialog-panel")
    private panel: HTMLElement;
    @query(".modal-dialog")
    private modalDialog: HTMLElement;
    @property({attribute: "disable-close-on-outside-click", type: Boolean})
    public disableCloseOnOutsideClick: boolean = false;

    @state()
    private open: boolean = false;

    public constructor(
        private dialogContent: HTMLElement,
        private dictionary: Dictionary,
        disableCloseOnOutsideClick: boolean = false,
        private eventBus: EventBus = resolve(EventBus),
        private page: Page = resolve(Page)
    ) {
        super();
        this.disableCloseOnOutsideClick = this.disableCloseOnOutsideClick || disableCloseOnOutsideClick;

        (this as DictionaryElement)._dictionary = dictionary;
        if (!dictionary) {
            this.dictionary = Dictionary.of(this);
        }
    }

    public render(): TemplateResult {
        return html`
            <dialog ?open=${this.open}
                    closedby="none"
                    @click=${this.closeOnOutsideClick}
                    @transitioncancel=${this.cleanUp}
                    @keydown=${this.closeOnEscape}
                    class="vertically-scrollable-area"
            >
                <div class="dialog-container">
                    <div class="dialog-panel">
                        <button class="close-button"
                                autofocus
                                aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")} data-eventelement="navigation"
                                data-tracking-label="modal-dialog-close"
                                @click=${this.close}
                                @transitioncancel=${this.cleanUp}
                        ></button>
                        ${this.dialogContent}
                        ${this.renderSecondaryCloseButton()}
                    </div>
            </dialog>
        `;
    }

    private renderSecondaryCloseButton(): TemplateResult | null {
        if (this.panel?.clientHeight > this.page.viewportHeight() * PANEL_SIZE_FACTOR) {
            return html`
                <div class="bottom-close-section layer-section-background ${this.backgroundClassFromSectionAbove()}">
                    <button class="secondary" data-eventelement="navigation" @click=${this.close} @transitioncancel=${this.cleanUp}>
                        <eop-msg key="MSG_CLOSE_DIALOG"></eop-msg>
                    </button>
                </div>
            `;
        } else {
            return null;
        }
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.bootStrapDialog();
    }

    private bootStrapDialog(): void {
        if (!this.dialogContent) {
            this.dialogContent = this.modalDialog;
            new BootStrapDialogOpener(this);
        }
    }

    public async showModal(): Promise<void> {
        await this.updateComplete;
        this.dialog.showModal();
        this.open = true;
        onTabbingOutOfDialog(this.dialog, () => this.close());
    }

    public async close(): Promise<void> {
        this.open = false;
        this.dialog.close();
        // remove once safari and firefox support overlay property in transitions, which can be waited on with transitioncancel
        // https://developer.mozilla.org/en-US/docs/Web/CSS/overlay
        await waitTime(CLOSING_ANIMATION_DURATION_IN_MILLIS);
        this.cleanUp();
    }

    private cleanUp(): void {
        this.eventBus.dispatchEvent(CLEANUP_DIALOG_EVENT, {});
    }

    private closeOnOutsideClick(e: Event): void {
        if (!this.disableCloseOnOutsideClick && eventOccurredOutside(e, this.panel)) {
            void this.close();
        }
    }

    private closeOnEscape(e: KeyboardEvent): void {
        if (e.key === "Escape" || e.key === "Esc") {
            e.stopPropagation();
            void this.close();
        }
    }

    private backgroundClassFromSectionAbove(): string {
        if (this.layerSections.length <= 0) {
            return "";
        }

        const classes = this.layerSections.item(this.layerSections.length - 1).classList.values();
        for (const className of classes) {
            if (className.includes("background-")) {
                return className;
            }
        }
        return "";
    }
}